import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { ImageViewer } from '@tecuity/pdf-viewer'
import { Spinner } from '../Loaders/Loaders'
import '../ImageViewer/ImageViewer.scss'
import * as api from '../../requests'
import { setErrorToast, setSuccessToast } from '../../actions'
import { SET_RECEIPT_WORKVIEW_ITEM } from '../Accounting/ReceiptValidation/ReceiptValidationReducer'
import Cookies from 'js-cookie'
import { useHistory } from 'react-router'
import './ImageCropTool.scss'

const ImageCropTool = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { selectedRow } = useSelector((state) => state.workview2, shallowEqual)
  const [bytes, setBytes] = useState(null)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [selectedGroupInfo, setSelectedGroupInfo] = useState({})
  const [activePageHeight, setActivePageHeight] = React.useState(0)

  const getBlobFromUrl = (myImageUrl) => {
    return new Promise((resolve, reject) => {
      let AUTH = localStorage.getItem('USER_AUTHENTICATION')
      let auth = AUTH && AUTH !== 'undefined' ? JSON.parse(AUTH) : {}

      if (!auth.ID) {
        console.debug('unable to find user from local storage. Checking cookies...')

        let specialUserId = Cookies.get('special-user-id')

        if (specialUserId && specialUserId !== '') {
          console.debug('user found in cookies. attaching credentials.')
          auth.ID = specialUserId
        }
      }

      let request = new XMLHttpRequest()
      request.open('GET', myImageUrl, true)
      request.setRequestHeader('Authorization', auth.ID)
      request.responseType = 'blob'
      request.onload = () => {
        resolve(request.response)
      }
      request.onerror = reject
      request.send()
    })
  }

  const getDataFromBlob = (myBlob) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(myBlob)
    })
  }

  // Navigate to the receipt so payment can be collected. The item is already in Pending Payment, so
  // if the receipt can't be resolved (or the user lacks ReceiptView) we fall back to the work view
  // rather than leaving it stuck.
  const goToReceipt = (receiptNum) => {
    setSaving(false)
    dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, workviewItem: selectedRow })
    history.push(
      receiptNum && appUserRights.includes('ReceiptView')
        ? `/accounting/receipt/${receiptNum}`
        : '/office/workview'
    )
  }

  // Approve the filing outright (nothing owed) and return to the work view.
  const approveFiling = () => {
    api.APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
      selectedRow.WorkViewItemId,
      () => history.push('/office/workview'),
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to approve the trademark filing.'))
        history.push('/office/workview')
      }
    )
  }

  // Resolve the receipt, then complete. Web filed/prepared uses the web-prepared method, which
  // returns the balance -> if nothing is owed we approve outright, otherwise we open the receipt to
  // collect payment. Paper uses the by-image-group lookup (number only, no balance) and opens the
  // receipt (paper filings still owe at this point).
  const routeToReceipt = () => {
    const onError = (err) => {
      console.error(err)
      history.push('/office/workview')
    }
    if (selectedRow.EntryTypeName && selectedRow.EntryTypeName.includes('Web')) {
      api.GET_WEB_PREPARED_RECEIPT_BALANCED(
        selectedRow.ImageEnvelopeId,
        ({ isReceiptBalanced, receiptNum }) =>
          isReceiptBalanced ? approveFiling() : goToReceipt(receiptNum),
        onError
      )
    } else {
      api.GET_RECEIPT_NUM_BY_IMAGE_GROUP_ID(selectedRow.ImageGroupId, goToReceipt, onError)
    }
  }

  // A successful crop (or an amendment finished without cropping) moves the WVI + filing to Pending
  // Payment FIRST -- unconditionally -- then hands off to the receipt/payment pipeline. The status
  // change is independent of the routing call, so if that call fails the item is never left stuck in
  // Pending Image Crop; it simply falls back to the work view already in Pending Payment.
  const finishFiling = () => {
    api.SET_TRADEMARK_TO_PENDING_PAYMENT(
      selectedRow.WorkViewItemId,
      routeToReceipt,
      (err) => {
        console.error(err)
        routeToReceipt()
      }
    )
  }

  const onSaveCroppedImage = (rects) => {
    const rect = rects && rects[0]
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      // No selection -> cropping is never mandatory (for any filing type). Skip the crop, leaving any
      // existing specimen (Azure + DB) untouched, and just finish the filing.
      setSaving(true)
      finishFiling()
      return
    }

    setSaving(true)
    const scaledElem = document.querySelector('.react-pdf__Document > div:last-of-type')
    // Scope to the main page container; the viewer also renders thumbnails as `.react-pdf__Page__canvas`.
    const pdfCanvas = scaledElem.querySelector('.react-pdf__Page__canvas')
    const scale = parseFloat(
      (scaledElem.getBoundingClientRect().width / scaledElem.offsetWidth).toFixed(1)
    )
    // activePageHeight (page.dimensions.Y) is in PDF points (72/in), but the backend crop service
    // renders the page at 300 DPI and crops in that pixel space (ImageCroppingService.CropAndRead ->
    // RenderPageToGdPictureImage(300)). Scale the coords from point space into 300-DPI pixel space,
    // otherwise the selection lands ~4.17x too small.
    const RENDER_DPI = 300
    const POINTS_PER_INCH = 72
    const multiplier =
      (activePageHeight / pdfCanvas.getBoundingClientRect().height) * scale * (RENDER_DPI / POINTS_PER_INCH)

    api.CROP_TRADEMARK_SPECIMEN(
      selectedGroupInfo.pages[currentPageIndex].DLN,
      {
        left: rect.left * multiplier,
        top: rect.top * multiplier,
        width: rect.width * multiplier,
        height: rect.height * multiplier,
      },
      () => {
        dispatch(setSuccessToast('Successfully Saved Cropped Image'))
        finishFiling()
      },
      (err) => {
        console.error(err)
        setSaving(false)
        dispatch(setErrorToast(err.Message))
      }
    )
  }

  useEffect(async () => {
    if (selectedRow && selectedRow.ImageEnvelopeId && selectedRow.ImageGroupId) {
      api.GET_IMAGE_ENVELOPE_BY_ID(
        selectedRow.ImageEnvelopeId,
        ({ groups }) => {
          const selectedGroup = groups.find((group) => group.groupId === selectedRow.ImageGroupId)
          if (selectedGroup) {
            setSelectedGroupInfo(selectedGroup)
          }
        },
        (err) => {
          console.error(err)
        }
      )
      try {
        let myBlob = await getBlobFromUrl(
          `${api.BASE_URL}/api/ImageEnvelope/GetImage/${selectedRow.ImageGroupId}/false`
        )
        let myImageData = await getDataFromBlob(myBlob)
        setBytes(myImageData)
      } catch (err) {
        console.error(err)
      }
    } else {
      dispatch(setErrorToast('No Work View Item Selected from Work View Manager'))
      history.push('/office/workview')
    }
  }, [selectedRow])

  useEffect(() => {
    if (selectedGroupInfo && selectedGroupInfo.pages) {
      setTitle(selectedGroupInfo.pages[currentPageIndex].type)
      setActivePageHeight(selectedGroupInfo.pages[currentPageIndex].dimensions.Y)
    }
  }, [selectedGroupInfo, currentPageIndex])

  return (
    <div className="crop-tool" style={{ position: 'relative' }}>
      {saving && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 1000,
          }}
        >
          <Spinner className="page-loader" themeColor="dark-neutral" />
          {/* The orbit spinner's dots swing ~16px below its tiny layout box, so the label needs
              extra top margin to clear them and sit visibly below the spinner rather than on it. */}
          <div style={{ marginTop: 40 }}>Saving cropped image…</div>
        </div>
      )}
      {/* Accent band so the crop tool reads as a distinct tool rather than the plain image viewer. */}
      <div className="crop-tool__header">
        <span className="crop-tool__icon" aria-hidden="true">✂</span>
        <div>
          <div className="crop-tool__title">Crop Specimen</div>
          <div className="crop-tool__instructions">
            Open <strong>MORE</strong> ▸ <strong>Crop Image</strong>, then drag a box around the mark.
            Leave the selection blank to keep the current specimen.
          </div>
        </div>
      </div>
      <div className="image-viewer-wrapper">
      <ImageViewer
          pdfBytes={bytes}
          title={title}
          currentPageIndex={currentPageIndex}
          onCurrentPageIndexChange={setCurrentPageIndex}
          imageViewerRights={{ 
            hasEditRights: false,
            hasPrintRights: false,
            hasExportRights: false,
            hasPrintQueueRights: false,
            hasCropRights: appUserRights.includes('ViewWorkview') 
          }}
          imageCropEnabled
          onSaveCroppedImage={onSaveCroppedImage}
        />
      </div>
    </div>
  )
}

export default ImageCropTool
